(() => {
  'use strict';
  if (customElements.get('cv-native-gallery')) return;

  const id = value => String(value ?? '').split('/').at(-1);
  const two = n => String(n).padStart(2, '0');
  const comparableImage = source => {
    if (!source) return '';
    try { return new URL(source, document.baseURI).pathname.replace(/_(?:\d+x\d*|x\d+)(?=\.)/i, ''); }
    catch { return ''; }
  };

  class CurvilaGallery extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.track = this.querySelector('[data-cvg-track]');
      if (!this.track) return;
      this.controller = new AbortController();
      this.scope = this.closest('.shopify-section') || this.parentElement;
      this.thumbs = this.querySelector('[data-cvg-thumbs]');
      this.dialog = this.querySelector('[data-cvg-dialog]');
      this.stage = this.querySelector('[data-cvg-stage]');
      this.browse = this.querySelector('[data-cvg-browse]');
      this.counts = [...this.querySelectorAll('[data-cvg-count]')];
      this.items = [...this.track.querySelectorAll('[data-cvg-item]')];
      this.index = 0;
      this.horizonSeq = 0;
      this.lastMedia = this.dataset.initialMedia || this.dataset.defaultMedia || '';
      this.browse.hidden = this.items.length < 2;
      this.thumbs.replaceChildren(...this.items.map((item,i) => this.thumbnail(item,i)));
      const initial = this.dataset.featured === 'true' && this.dataset.initialMedia ? this.items.findIndex(item => item.dataset.mediaId === this.dataset.initialMedia) : 0;
      this.select(Math.max(0, initial));
      this.on(this, 'click', e => this.click(e));
      this.on(this, 'keydown', e => this.keydown(e));
      this.on(this.track, 'scroll', () => this.scrolled(), {passive:true});
      this.on(this.dialog, 'cancel', () => { this.restoreFocus = true; this.stopPlayer(); });
      this.on(this.dialog, 'close', () => this.closed());
      this.on(this.dialog, 'click', e => {
        if (e.target !== this.dialog) return;
        const b = this.dialog.getBoundingClientRect();
        if (e.clientX < b.left || e.clientX > b.right || e.clientY < b.top || e.clientY > b.bottom) this.close();
      });
      this.on(this.scope, 'cv-native:variant', e => this.variant(e.detail || {}));
      this.on(this.scope, 'shopify:product:select', e => this.horizon(e));
      if (window.ResizeObserver) {
        this.observer = new ResizeObserver(() => this.align(this.index));
        this.observer.observe(this.track);
      }
      const url = new URL(location.href);
      if (url.searchParams.has('variant') || url.searchParams.has('option_values')) {
        const picked = this.scope?.querySelector('cv-native-purchase')?.getSelection?.();
        if (picked) this.variant(picked, true);
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      cancelAnimationFrame(this.scrollFrame);
      this.stopPlayer();
      if (this.dialog?.open) this.dialog.close();
    }

    on(node, type, handler, options = {}) {
      node?.addEventListener(type, handler, {...options, signal:this.controller.signal});
    }

    variant(detail, force = false) {
      if (this.dataset.featured !== 'true') return;
      if (detail.sectionId && String(detail.sectionId) !== this.dataset.section) return;
      if (detail.productId && id(detail.productId) !== this.dataset.product) return;
      const mediaId = id(detail.featuredMediaId || detail.variant?.featured_media?.id);
      const image = detail.featuredImage || detail.variant?.featured_image;
      const source = comparableImage(typeof image === 'string' ? image : image?.src || image?.url);
      const index = this.items.findIndex(item => (mediaId && item.dataset.mediaId === mediaId) || (source && comparableImage(item.dataset.src) === source));
      const next = index < 0 ? '' : this.items[index].dataset.mediaId || source;
      if (index >= 0 && (force || next !== this.lastMedia)) this.select(index);
      this.lastMedia = next;
    }

    async horizon(event) {
      if (!event.promise || event.target.closest?.('product-card, quick-add-dialog')) return;
      const seq = ++this.horizonSeq;
      try {
        const result = await event.promise;
        if (!this.isConnected || seq !== this.horizonSeq) return;
        const fresh = result?.detail?.html?.querySelector('cv-native-gallery[data-section="' + CSS.escape(this.dataset.section) + '"]');
        if (fresh && fresh.dataset.product !== this.dataset.product) {
          this.replaceWith(document.importNode(fresh, true));
          return;
        }
        const variant = result?.variant || {};
        this.variant({
          productId: fresh?.dataset.product || event.product?.id,
          variantId: variant.id,
          options: variant.selectedOptions || event.selectedOptions,
          featuredMediaId: fresh?.dataset.initialMedia || variant.featured_media?.id
        });
      } catch { /* A newer option selection can abort the previous section request. */ }
    }

    thumbnail(el,i) {
      const btn = document.createElement('button');
      btn.type = 'button'; btn.className = 'cvg__thumb'; btn.dataset.cvgThumb = String(i);
      btn.setAttribute('aria-label',[this.dataset.view + ' ' + (i+1),el.dataset.alt].filter(Boolean).join('. '));
      const img = document.createElement('img');
      img.alt = ''; img.width = 152; img.height = 190; img.draggable = false;
      img.loading = i ? 'lazy' : 'eager'; img.decoding = 'async'; img.src = el.dataset.thumb || el.dataset.src;
      btn.append(img); return btn;
    }

    align(index) {
      if (!this.items.length || !this.track.clientWidth) return;
      const target = index * this.track.clientWidth;
      if (Math.abs(this.track.scrollLeft - target) > 1) this.track.scrollTo({left:target,behavior:'instant'});
    }

    select(index, align = true) {
      const total = this.items.length;
      if (!total) return;
      this.index = (index % total + total) % total;
      this.items.forEach((el,i) => { const a = el.querySelector('[data-cvg-open]'); if (a) a.tabIndex = i === this.index ? 0 : -1; });
      [...this.thumbs.children].forEach((btn,i) => btn.setAttribute('aria-current',String(i === this.index)));
      this.counts.forEach(node => { node.textContent = two(this.index+1)+' / '+two(total); });
      if (align) this.align(this.index);
      const thumb = this.thumbs.children[this.index];
      if (thumb) {
        const start = thumb.offsetLeft - this.thumbs.offsetLeft;
        const right = start + thumb.offsetWidth;
        if (start < this.thumbs.scrollLeft) this.thumbs.scrollLeft = start;
        else if (right > this.thumbs.scrollLeft + this.thumbs.clientWidth) this.thumbs.scrollLeft = right-this.thumbs.clientWidth;
      }
      if (this.dialog.open) this.viewLarge();
    }

    scrolled() {
      cancelAnimationFrame(this.scrollFrame);
      this.scrollFrame = requestAnimationFrame(() => {
        if (!this.track.clientWidth || !this.items.length) return;
        const i = Math.min(this.items.length-1,Math.max(0,Math.round(this.track.scrollLeft/this.track.clientWidth)));
        if (i !== this.index) this.select(i,false);
      });
    }

    click(event) {
      const el = event.target.closest?.('a,button');
      if (!el || !this.contains(el)) return;
      if (el.hasAttribute('data-cvg-step')) this.select(this.index + Number(el.dataset.cvgStep));
      else if (el.hasAttribute('data-cvg-thumb')) this.select(Number(el.dataset.cvgThumb));
      else if (el.hasAttribute('data-cvg-close')) this.close();
      else if (el.hasAttribute('data-cvg-enlarge')) this.open(this.index,el);
      else if (el.hasAttribute('data-cvg-open') && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
        event.preventDefault(); this.open(this.items.indexOf(el.closest('[data-cvg-item]')),el);
      }
    }

    keydown(event) {
      if (!this.items.length || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.target.closest('video,iframe,model-viewer')) return;
      const home = event.key === 'Home', end = event.key === 'End';
      const dir = event.key === 'ArrowRight' ? 1 : (event.key === 'ArrowLeft' ? -1 : 0);
      if (!home && !end && !dir) return;
      event.preventDefault(); this.select(home ? 0 : (end ? this.items.length-1 : this.index+dir));
      if (event.target.hasAttribute('data-cvg-thumb')) this.thumbs.children[this.index]?.focus({preventScroll:true});
      if (event.target.hasAttribute('data-cvg-open')) this.items[this.index]?.querySelector('a')?.focus({preventScroll:true});
    }

    open(i, trigger) {
      if (i < 0 || !this.items[i]) return;
      this.returnTo = trigger; this.restoreFocus = true; this.select(i);
      if (!this.dialog.open) {
        if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
        else this.dialog.setAttribute('open','');
      }
      this.viewLarge();
      this.dialog.querySelector('[data-cvg-close]')?.focus({preventScroll:true});
    }

    stopPlayer() {
      this.stage?.querySelectorAll('video').forEach(el => el.pause());
      this.stage?.replaceChildren();
    }

    viewLarge() {
      const el = this.items[this.index];
      if (!el) return;
      this.stopPlayer();
      const player = el.querySelector('template[data-cvg-player]');
      if (player) {
        this.stage.append(player.content.cloneNode(true));
        const model = this.stage.querySelector('model-viewer');
        if (model && window.Shopify?.loadFeatures) window.Shopify.loadFeatures([{name:'model-viewer-ui',version:'1.0',onLoad:errors=>{
          if (!errors && model.isConnected && window.Shopify.ModelViewerUI) new window.Shopify.ModelViewerUI(model);
        }}]);
        return;
      }
      const photo = document.createElement('img');
      photo.src = el.dataset.src; photo.alt = el.dataset.alt || ''; photo.decoding = 'async';
      this.stage.append(photo);
    }

    close() {
      if (!this.dialog?.open) return;
      this.stopPlayer();
      if (typeof this.dialog.close === 'function') this.dialog.close();
      else { this.dialog.removeAttribute('open'); this.closed(); }
    }

    closed() {
      this.stopPlayer();
      if (this.restoreFocus && this.returnTo?.isConnected) this.returnTo.focus({preventScroll:true});
      this.returnTo = null;
    }

  }

  customElements.define('cv-native-gallery', CurvilaGallery);
})();
